// Libs
import classNames from 'classnames/bind';
import { Avatar, Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BaseMoreAction, BaseTextEditor } from '~/components/common';
// Others
import { INote } from '~/utils/interface/note';
import { formatDate, getFullName, getInitials } from '~/utils/helper/common';
import { DateFormatEnum, ModeTextEditorEnum, ThemeTextEditorEnum } from '~/utils/enum/common';
import { EMPTY_STRING } from '~/utils/constants/common';
// Styles, images, icons
import styles from './NoteItem.module.scss';
import { icons } from '~/assets';

type Props = {
  note?: INote;
  onEdit?: (note: INote) => void;
  onDelete?: (note: INote) => void;
};

const cx = classNames.bind(styles);

const NoteItem = (props: Props) => {
  //#region Destructuring Props
  const { note, onEdit, onDelete } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Box component={'div'} id='noteItemComponent' className={cx('container')}>
      <Stack
        component={'div'}
        alignItems={'center'}
        justifyContent={'space-between'}
        gap={'20px'}
        direction={'row'}
        className={cx('userInfo')}
      >
        <Stack
          component={'div'}
          className={cx('createdBy')}
          direction={'row'}
          alignItems={'center'}
          gap={'12px'}
        >
          {note?.avatarImage ? (
            <Avatar src={note?.avatarImage} alt={t('common_alt_img')} className={cx('avatar')} />
          ) : (
            <Avatar src={note?.avatarImage} alt={t('common_alt_img')} className={cx('avatar')}>
              <Typography className={cx('avatarUserName')}>{getInitials({ ...note })}</Typography>
            </Avatar>
          )}

          <Stack spacing={'4px'}>
            <Typography component={'span'} className={cx('username')}>
              {getFullName({ ...note })}
            </Typography>

            <Typography className={cx('dateTime')}>
              {note?.createdAt
                ? formatDate(note?.createdAt, DateFormatEnum.MM_DD_YYYY_HH_AM_PM)
                : EMPTY_STRING}
            </Typography>
          </Stack>
        </Stack>

        <Stack
          component={'div'}
          className={cx('action')}
          direction={'row'}
          alignItems={'center'}
          gap={'12px'}
        >
          {(onEdit || onDelete) && (
            <BaseMoreAction
              icon={icons.commonIconMoreInactive}
              actions={[
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => {
                    onEdit && note && onEdit(note);
                  },
                },
                {
                  hasOtherColor: true,
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => {
                    onDelete && note && onDelete(note);
                  },
                },
              ]}
            />
          )}
        </Stack>
      </Stack>

      <Box>
        <BaseTextEditor
          mode={ModeTextEditorEnum.VIEW}
          theme={ThemeTextEditorEnum.BUBBLE}
          value={note?.content}
          width={'100%'}
        />
      </Box>
    </Box>
  );
};

export default NoteItem;
